/**
 * 라이브 이벤트 계약 (v33, `docs/67-live-event-hybrid-channel-design.md`).
 *
 * ── ★ 이 파일이 BLOOM 이 시각을 말하는 유일한 자리다 ──────────
 *
 * 절대 규칙 75 는 「BLOOM Server 는 시각을 한 자도 말하지 않는다」였고, v33 이 **예외 둘**을
 * 만든다 — `LiveEventWindow.startAtMs`·`endAtMs`. 그 둘은 **사람이 콘솔에 박은 값**이지
 * 서버가 계산한 편성 시각이 아니다. 그래서 `Schedule` 에는 v33 이후에도 시각 컬럼이 없고
 * (규칙 73 ④의 금지 셋 첫째), 개인화 편성의 모양은 한 글자도 달라지지 않는다.
 *
 * ── 축이 둘이고 싣는 것이 다르다 ─────────────────────────────
 *
 * | 축 | 타입 | 주소 |
 * |---|---|---|
 * | 재생 (`PlaylistResponse.live`) | `LiveEventWindow` | **있다** — 리시버가 플레이어에게 넘긴다 |
 * | 조망 (`ChannelLineup.liveEvents`) | `LiveEventSlot` | **없다** (절대 규칙 68 의 단서) |
 *
 * ONAIR 는 `ChannelSummary.onair.streamUrl` 을 조망에 실었고 근거는 **「잃을 것이 없다」**
 * 였다(그 채널에는 `select`·회전·`playlistId` 가 애초에 없다). **혼합 채널에는 셋 다 있다** —
 * 조망에 주소를 실으면 편성표가 리시버를 우회해 라이브를 틀 수 있게 되고, 그때 잃는 것이
 * 정확히 그 셋이다. 그리고 **필요가 없다**: 라이브는 리시버가 틀고 리시버는 `/v1/playlist`
 * 로 주소를 받는다.
 */
// ---------------------------------------------------------------------------
// v34. 라이브 큐 — 창이 열렸는가를 **한 함수가** 계산한다 (`docs/69` §2.2 · 절대 규칙 86)
// ---------------------------------------------------------------------------
/**
 * 개시·종료의 근거 (v34). `time` 은 예정 시각에 저절로, `cue` 는 사람이 누를 때까지 기다린다.
 * **시각 방식 이벤트에도 큐를 쏠 수 있다** — 개시 큐는 「일찍 시작」, 종료 큐는 「일찍 끝냄」이다
 * (명세서 [0171]).
 */
export const LIVE_CUE_MODES = ['time', 'cue'];
/**
 * 라이브 이벤트의 지금 (v34). **저장하지 않는다** — 칸 여섯과 지금 시각이면 하나로 정해진다
 * (절대 규칙 86).
 *
 * | phase | 뜻 | 창 |
 * |---|---|---|
 * | `scheduled` | 예정 시각 전 | 닫힘 |
 * | `waiting` | 예정은 지났고 **개시 큐를 기다린다** | **닫힘** — 리시버는 녹화물, 스트림은 503 |
 * | `running` | 방송 중 | 열림 |
 * | `extended` | 예상 종료는 지났고 **종료 큐를 기다린다** | 열림 |
 * | `ended` | 끝났다 | 닫힘 |
 */
export const LIVE_EVENT_PHASES = ['scheduled', 'waiting', 'running', 'extended', 'ended'];
/**
 * 열린 종료(종료 큐를 기다린다)가 저절로 닫히는 길이 — **실제 시작부터** (명세서 [0167]).
 *
 * 종료 큐를 잊어도 채널이 영원히 라이브가 되지 않게 한다. **행을 고치지 않고 계산으로 닫는다** —
 * 이 서버에는 타이머가 없다(`actualEndAt` 은 빈 채로 phase 만 `ended`).
 */
export const LIVE_EVENT_OPEN_END_MAX_MS = 6 * 60 * 60 * 1000;
/**
 * 편성표가 「연장 중」 상자의 끝단을 지금 선보다 이만큼 오른쪽에 둔다 (명세서 [0179] · 도 17).
 * 끝을 모르므로 폭을 정할 근거가 없고, 「끝이 아직 앞에 있다」를 보이는 최소 폭이다.
 */
export const LIVE_EVENT_EXTEND_PREVIEW_MS = 5 * 60 * 1000;
/**
 * ★ **창이 열렸는가를 계산하는 유일한 함수** (v34 · `docs/69` §2.2 · 절대 규칙 86).
 *
 * ```
 * 실효 시작 S = 실제 시작 ?? (개시 방식 = 시각 ? 예정 시작 : 없음)
 * 실효 종료 E = 실제 종료 ?? (종료 방식 = 시각 ? 예상 종료 : 열림 → S + 6h)
 * ```
 *
 * 경계는 v33 그대로 `[S, E)` 다 — 시작 순간은 안, 종료 순간은 밖. 채널 스트림 서버의 503 과
 * 리시버의 「끝났다」가 같은 순간이어야 한다(`liveWindowAt` 의 ★).
 *
 * **서버의 선별 · 채널 스트림 서버 · 리시버 · 편성표 · 홈 · 콘솔 · 큐 앱이 전부 이것을 부른다.**
 * 두 벌이 되는 순간 「큐 앱은 방송 중이라는데 화면은 안 바뀐다」가 생기고 원인이 화면에 없다.
 */
export function liveEffectiveWindow(t, nowMs) {
    const actualStart = t.actualStartAtMs ?? null;
    const actualEnd = t.actualEndAtMs ?? null;
    const startMs = actualStart ?? ((t.startMode ?? 'time') === 'time' ? t.startAtMs : null);
    const explicitEnd = actualEnd ?? ((t.endMode ?? 'time') === 'time' ? t.endAtMs : null);
    const openEnd = explicitEnd === null;
    const endMs = explicitEnd ?? (startMs === null ? null : startMs + LIVE_EVENT_OPEN_END_MAX_MS);
    // 종료 큐가 왔으면 무엇보다 먼저 끝이다(개시 없이 올 수는 없지만 — 서버가 409 로 막는다 —
    // 오더라도 창이 열리지 않게).
    if (actualEnd !== null && nowMs >= actualEnd) {
        return { phase: 'ended', startMs, endMs, openEnd };
    }
    if (startMs === null) {
        /*
         * ★ v34-c. **개시 큐를 받지 못한 채 예정 종료가 지나면 만료다 — 없던 것이 된다** (2026-09-24 ·
         * `docs/69` §2.2 · 절대 규칙 86). **종료 방식과 무관하게 예정 종료다.** 옛 판은 종료가 큐면
         * 예정 시작 + 6시간까지 기다렸는데, 그 사이 뒤 이벤트의 예정 시각이 오면 낡은 대기가 큐 앱의
         * 「다음」에 먼저 서고, 늦게 열면 **첫 순간부터 「연장 중」**이라 뒤 이벤트의 예정 창을 덮었다
         * (무대 · `docs/70` §7). 그래서 **어떤 개시 큐도 예정 종료 뒤에는 열지 못한다** — 서버가 409
         * `LIVE_EVENT_EXPIRED` 로 막는다(`liveEventExpired`).
         */
        if (nowMs >= t.endAtMs)
            return { phase: 'ended', startMs, endMs, openEnd };
        return { phase: nowMs < t.startAtMs ? 'scheduled' : 'waiting', startMs, endMs, openEnd };
    }
    if (nowMs < startMs)
        return { phase: 'scheduled', startMs, endMs, openEnd };
    if (endMs !== null && nowMs >= endMs)
        return { phase: 'ended', startMs, endMs, openEnd };
    if (openEnd && nowMs >= t.endAtMs)
        return { phase: 'extended', startMs, endMs, openEnd };
    return { phase: 'running', startMs, endMs, openEnd };
}
/**
 * v34-c. **만료** — 개시 큐를 받지 못한 채 예정 종료가 지났다. **방송된 적이 없다**(끝난 것과 다르다).
 * 서버가 개시 큐의 409 를 둘(`LIVE_EVENT_EXPIRED` · `LIVE_EVENT_ALREADY_ENDED`)로 가르는 근거다.
 */
export function liveEventExpired(t, nowMs) {
    const eff = liveEffectiveWindow(t, nowMs);
    return eff.phase === 'ended' && eff.startMs === null;
}
/** phase 만 — `liveEffectiveWindow(t, now).phase` */
export function liveEventPhase(t, nowMs) {
    return liveEffectiveWindow(t, nowMs).phase;
}
/** **창이 열려 있다** = 방송 중 또는 연장 중. `waiting` 은 열린 창이 아니다 */
export function isLiveOpen(phase) {
    return phase === 'running' || phase === 'extended';
}
/** 리시버의 조회 주기 (v34 · `docs/69` §5.2 · 명세서 [0172]) */
export const LIVE_POLL_FAST_MS = 1000;
/** 먼 창만 있을 때 — 다시 깐 창을 받기 위해서만 */
export const LIVE_POLL_SLOW_MS = 30_000;
/** 예정 시작이 이만큼 안으로 들어오면 빠른 주기로 바꾼다 */
export const LIVE_POLL_LEAD_MS = 5 * 60 * 1000;
/**
 * 다음 조회까지 기다릴 시간(ms). **`null` 이면 조회하지 않는다**(창이 없는 채널).
 *
 * 빠른 주기(1초) — 바이패스 중이거나, 대기·방송 중·연장 중인 창이 있거나, 5분 안에 예정 시작이
 * 있다. 그 밖(먼 창만)은 30초. 큐가 닿는 시간은 이 주기 + 다음 `timeupdate` 다.
 */
export function livePollIntervalMs(windows, nowMs, inBypass) {
    if (inBypass)
        return LIVE_POLL_FAST_MS;
    if (windows.length === 0)
        return null;
    for (const w of windows) {
        const phase = liveEventPhase(w, nowMs);
        if (phase === 'waiting' || phase === 'running' || phase === 'extended')
            return LIVE_POLL_FAST_MS;
        if (phase === 'scheduled' && w.startAtMs - nowMs <= LIVE_POLL_LEAD_MS)
            return LIVE_POLL_FAST_MS;
    }
    return LIVE_POLL_SLOW_MS;
}
/**
 * 재생 경로에 싣는 창의 상한. **넘치면 가까운 것부터 자른다.**
 *
 * 배열인 것이 결정이다(`docs/67` D23) — 하나만 실으면 첫 이벤트가 끝날 때 리시버가
 * 플레이리스트를 다시 받아야 하고, 그 재요청은 `resync()` 이며 그것은 **복귀 지점을
 * 「서버가 아는 것」 하나로 줄인다**(비로그인에게는 아무것도 남지 않는다).
 */
export const LIVE_EVENT_MAX_WINDOWS = 8;
/** 이만큼 앞의 이벤트까지만 싣는다(ms). 하루면 데모의 어떤 무대도 덮는다 */
export const LIVE_EVENT_HORIZON_MS = 24 * 60 * 60 * 1000;
/**
 * 종료 뒤 **유예**(ms). 이 동안 채널 스트림 서버는 마지막 창을 `#EXT-X-ENDLIST` 와 함께
 * 낸다 (`docs/67` §5.2).
 *
 * ★ **리시버가 제때 비켜서지 못한 경우를 위한 것이다** — 기기 시계가 조금 늦으면 종료
 * 시각을 넘겨 요청하는데, 그때 503 을 주면 **오류 스크림**이 뜬다. `ENDLIST` 하나가
 * 「끝났다」를 말하고 플레이어는 오류 대신 마지막 프레임에 머문다.
 */
export const LIVE_EVENT_GRACE_MS = 30_000;
/**
 * 이벤트 한 건의 길이 상한(시간). 데모가 「향후 6시간 안」이라 그 값이다.
 *
 * **상한이 있는 이유는 실수를 막는 것 하나다** — 종료 시각을 연도까지 잘못 넣으면
 * 그 채널이 **영원히 라이브**가 되고, 되돌리려면 진행 중 수정 잠금(`LIVE_EVENT_RUNNING`)
 * 을 먼저 풀어야 한다.
 */
export const LIVE_EVENT_MAX_HOURS = 6;
/** `/live.m3u8` 경로 — ONAIR 와 **같은 경로**이고 질의가 하나 는다 */
export const LIVE_EVENT_STREAM_PATH = '/live.m3u8';
/**
 * 이벤트 스트림의 절대 URL. **서버만 부른다** (앱·리시버는 받은 값을 그대로 쓴다).
 *
 * `buildOnairStreamUrl` 과 **같은 경로에 `eventId` 를 더한 것**이다 — 채널 스트림 서버는
 * `eventId` 가 있으면 유한 창, 없으면 무한 루프다(`docs/67` §5.1).
 */
export function buildLiveEventStreamUrl(baseUrl, channelId, eventId) {
    const base = baseUrl.replace(/\/+$/, '');
    return `${base}${LIVE_EVENT_STREAM_PATH}?channelId=${encodeURIComponent(channelId)}&eventId=${encodeURIComponent(eventId)}`;
}
/**
 * 지금이 창 안인가 · 다음 창은 무엇인가. **리시버와 화면이 같은 함수를 부른다.**
 *
 * ── ★ 경계의 규율 ───────────────────────────────────────────
 *
 * **시작 시각 그 순간은 창 안이고, 종료 시각 그 순간은 창 밖이다**(`[start, end)`).
 * `alignToChunkStart` 의 `<` 와 같은 규율이고, 그래야 「끝났다」 판정과 채널 스트림
 * 서버의 503 이 **같은 순간**에 일어난다. 반대로 두면 리시버가 1틱 동안 이미 죽은 창을
 * 물고 있다.
 *
 * ── `current` 와 `next` 가 동시에 있을 수 있다 ──────────────
 *
 * 이벤트가 잇달아 있으면 그렇다. 리시버는 `current` 를 먼저 보고, 없을 때만 `next` 의
 * 컷을 무장한다.
 *
 * @param windows 순서를 가정하지 않는다 — 서버가 시각 순으로 주지만 여기서 다시 고른다
 * @returns 짝이 없으면 `null`. **빈 창(`endAtMs <= startAtMs`)은 무시한다**
 */
export function liveWindowAt(windows, nowMs) {
    let current = null;
    let currentStart = Infinity;
    let next = null;
    for (const w of windows) {
        // v34. **판정은 `liveEffectiveWindow` 가 한다**(절대 규칙 86) — 시각 방식이면 v33 과 같은
        // 답이고(빈 창은 열리지 않는다), 큐 방식이면 개시 큐 전(`waiting`)이 창 밖이다.
        const eff = liveEffectiveWindow(w, nowMs);
        if (isLiveOpen(eff.phase)) {
            // 겹침은 서버가 막지만(409), 오더라도 **먼저 시작한 것**을 고른다 — 판정이
            // 배열 순서에 매이지 않게. 연장이 다음 예정 창을 덮을 때도 같은 규율이다(`docs/69` R3).
            const start = eff.startMs ?? w.startAtMs;
            if (current === null || start < currentStart) {
                current = w;
                currentStart = start;
            }
            continue;
        }
        // 다음 창 — 아직 열리지 않았고 끝나지도 않은 것 중 예정 시작이 가장 이른 것.
        if (!(w.endAtMs > w.startAtMs) && (w.startMode ?? 'time') === 'time')
            continue;
        if (eff.phase !== 'scheduled' && eff.phase !== 'waiting')
            continue;
        if (next === null || w.startAtMs < next.startAtMs)
            next = w;
    }
    return { current, next };
}
/**
 * 절대 시각 → 「지금」 기준 초. **ONAIR 의 `onairSpansAt` 과 같은 뺄셈이다.**
 *
 * 「흐르는가」는 `if` 가 아니라 **그 값이 절대 시각 기준으로 정의됐는가**에서 나온다
 * (`docs/55` §3 이 세운 좌표계 · 절대 규칙 73). 그래서 이 함수의 결과를 쓰는 상자는
 * 저절로 흐르고, 같은 행의 녹화 상자는 저절로 안 흐른다.
 */
export function liveSpanOf(slot, nowMs) {
    /*
     * v34. **시작단·끝단을 phase 가 고른다** (`docs/69` §7.2 · 명세서 [0179] · 도 17).
     *
     * | phase | 시작단 | 끝단 |
     * |---|---|---|
     * | `scheduled` | 예정 시작 | 예상 종료 |
     * | `waiting` | **지금**(선에 붙어 함께 간다) | 예상 종료 — 그때 만료된다(v34-c) |
     * | `running` | 실효 시작 | 실효 종료(열린 종료면 예상 종료) |
     * | `extended` | 실효 시작 | **지금 + 5분**(함께 간다) |
     * | `ended` | 실효 시작 | 실효 종료 |
     *
     * **시각 방식이면 v33 과 같은 값이다** — 실효 시작·종료가 곧 예정 둘이다. 「흐르는가」는 여전히
     * `if` 가 아니라 **좌표가 절대 시각 기준인가**에서 나온다(지금 선에 붙은 단은 `nowMs` 에서 0).
     */
    const eff = liveEffectiveWindow(slot, nowMs);
    const sec = (ms) => (ms - nowMs) / 1000;
    const preview = sec(nowMs + LIVE_EVENT_EXTEND_PREVIEW_MS);
    switch (eff.phase) {
        case 'waiting':
            // v34-c — 대기는 예정 종료 전에만 있다(그때 만료된다). 끝단은 언제나 앞에 있다.
            return {
                startSec: 0,
                endSec: sec(slot.endAtMs),
                phase: eff.phase,
                openEnd: eff.openEnd,
            };
        case 'extended':
            return {
                startSec: sec(eff.startMs ?? slot.startAtMs),
                endSec: preview,
                phase: eff.phase,
                openEnd: true,
            };
        case 'running':
            return {
                startSec: sec(eff.startMs ?? slot.startAtMs),
                endSec: sec(eff.openEnd ? slot.endAtMs : (eff.endMs ?? slot.endAtMs)),
                phase: eff.phase,
                openEnd: eff.openEnd,
            };
        case 'ended':
            return {
                startSec: sec(eff.startMs ?? slot.startAtMs),
                endSec: sec(eff.endMs ?? slot.endAtMs),
                phase: eff.phase,
                openEnd: eff.openEnd,
            };
        case 'scheduled':
        default:
            return {
                startSec: sec(eff.startMs ?? slot.startAtMs),
                endSec: sec(eff.openEnd ? slot.endAtMs : (eff.endMs ?? slot.endAtMs)),
                phase: eff.phase,
                openEnd: eff.openEnd,
            };
    }
}
//# sourceMappingURL=live.js.map